import React, { CSSProperties, PropsWithChildren, ReactElement } from 'react'
import { SkeletonWrapper } from './styles'

export interface SkeletonStyleProps {
  baseColor?: string
  highlightColor?: string

  width?: string | number
  height?: string | number
  borderRadius?: string | number
  inline?: boolean

  duration?: number
  animationDirection?: 'ltr' | 'rtl'
  enableAnimation?: boolean
}

export interface SkeletonProps extends SkeletonStyleProps {
  count?: number
  wrapper?: React.FunctionComponent<PropsWithChildren<unknown>>

  className?: string
  containerClassName?: string
  containerTestId?: string

  circle?: boolean
  style?: CSSProperties
  wrapperStyle?: CSSProperties
}

export function Skeleton({
  count = 1,
  wrapper: Wrapper,

  className: customClassName,
  containerClassName,
  containerTestId,

  circle = false,

  style: styleProp,
  wrapperStyle,
  ...originalPropsStyleOptions
}: SkeletonProps): ReactElement {
  const customStyle = { ...originalPropsStyleOptions, circle }

  let className = 'react-loading-skeleton'
  if (customClassName) className += ` ${customClassName}`

  const inline = customStyle.inline ?? false

  const elements: ReactElement[] = []

  const countCeil = Math.ceil(count)

  for (let i = 0; i < countCeil; i++) {
    if (countCeil > count && i === countCeil - 1) {
      // count is not an integer and we've reached the last iteration of
      // the loop, so add a "fractional" skeleton.
      //
      // For example, if count is 3.5, we've already added 3 full
      // skeletons, so now we add one more skeleton that is 0.5 times the
      // original width.

      const width = customStyle?.width ?? styleProp?.width ?? '100%'
      const fractionalPart = count % 1
      const fractionalWidth = typeof width === 'number' ? width * fractionalPart : `calc(${width} * ${fractionalPart})`

      customStyle.width = fractionalWidth
    }

    const skeletonSpan = (
      <SkeletonWrapper key={i} className={className} {...customStyle} style={{ ...styleProp }}>
        &zwnj;
      </SkeletonWrapper>
    )

    if (inline) {
      elements.push(skeletonSpan)
    } else {
      elements.push(
        <React.Fragment key={i}>
          {skeletonSpan}
          <br />
        </React.Fragment>
      )
    }
  }

  return (
    <span
      className={containerClassName}
      data-testid={containerTestId}
      aria-live="polite"
      aria-busy={customStyle.enableAnimation ?? true}
      style={{ ...wrapperStyle }}
    >
      {Wrapper ? elements.map((el, i) => <Wrapper key={i}>{el}</Wrapper>) : elements}
    </span>
  )
}
